import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { RenderIf } from 'react-rainbow-components';
import SaraAvatar from 'components/icons/saraAvatar';
import { FormattedDate } from 'react-intl';
import { HasRoles } from '@voxityai/auth/components';
import UserAvatar from 'components/UserAvatar';
import { PaidAdChip } from 'components/PaidAd';
import readMessageAdReferral from 'components/PaidAd/readMessageAdReferral';
import getInitials from 'data/services/string/getInitials';
import getDisplayName from 'data/services/profile/getDisplayName';
import formatIntlPhoneNumber from 'data/services/phone/formatter';
import { Message } from 'data/firestore/agent/thread/message/types';
import { auth } from 'data/firestore/firebase';
import { EntityGet } from 'data/firestore/types';
import {
    Channel, ItemUser, MetaAdReferral, MultimodalPart,
} from 'data/firestore/globals';
import { useCurrentUser } from '@voxityai/auth/hooks';
import Download from 'components/icons/download';
import WrenchIcon from 'components/icons/wrench';
import ReportButton from 'components/ReportButton';
import { createDownloadLink } from 'components/SMSChat/ChatMessages/Message/MediaContent/helpers/createDownloadLink';
import { Thread } from 'data/firestore/agent/thread/types';
import removeActionsTokens from '../../../../helpers/removeActionsTokens';
import {
    ChatBubble,
    AuthorContainer,
    AuthorName,
    Time,
    MessageContainer,
    MessageContent,
    BubbleTopContainer,
    BubbleBottomContainer,
    FromAd,
    ReportButtonContainer,
    ToolCallsButton,
} from './styled';
import Body from './Body';
import Media from './Media';
import getMediaComponent from './Media/helpers/getMediaComponent';
import { MediaContainer, StyledButtonIcon } from './Media/styled';
import ToolCallsModal, { ResolvedToolCall } from './ToolCallsModal';

type ToolMessageEntity = EntityGet<Message> & { role: 'tool' };

interface MessageBubbleProps {
    message: Extract<EntityGet<Message>, { role: 'user' | 'assistant' }>;
    createdBy: ItemUser;
    thread?: Thread;
    toolMsgMap?: Record<string, ToolMessageEntity>;
    adReferral?: MetaAdReferral | null;
}

const FromAdMarker = ({ referral }: { referral: MetaAdReferral }) => (
    <FromAd>
        <PaidAdChip referral={referral} variant="message" />
    </FromAd>
);

// Type guard to check if content is an array of MultimodalPart
const isMultimodalContent = (
    content: string | Array<MultimodalPart>,
): content is Array<MultimodalPart> => Array.isArray(content);

// Get URL from binary part (handles url, data, or id)
const getBinaryPartUrl = (part: MultimodalPart): string | undefined => {
    if (part.type !== 'binary') return undefined;

    // Priority: url > data > id
    if (part.url) return part.url;
    if (part.data) return `data:${part.mimeType};base64,${part.data}`;
    if (part.id) return part.id; // Assuming id can be used as URL

    return undefined;
};

const MessageBubble = ({
    message, createdBy, thread, toolMsgMap = {}, adReferral: adReferralProp,
}: MessageBubbleProps) => {
    const adReferral = readMessageAdReferral(message) || adReferralProp;
    const { content, media = [], id: messageId } = message;
    const { uid: createdByUid, role } = createdBy;
    const { user: currentUser } = useCurrentUser({ auth });
    const { agentId = '', threadId = '' } = useParams<{ agentId: string; threadId: string }>();
    const [isToolCallsModalOpen, setIsToolCallsModalOpen] = useState(false);

    const isContentArray = isMultimodalContent(content);
    const strippedContent = useMemo(
        () => (typeof content === 'string' ? removeActionsTokens(content) : null),
        [content],
    );

    const direction = useMemo(() => {
        if (role === 'user' && currentUser?.uid === createdByUid) return 'outgoing' as const;
        return 'incoming' as const;
    }, [createdByUid, role, currentUser]);

    const authorDisplayName = useMemo(() => getDisplayName(
        createdBy,
        formatIntlPhoneNumber(createdBy?.phoneNumber as string),
    ), [createdBy]);

    const avatar = useMemo(() => {
        if (role === 'agent') {
            return <SaraAvatar />;
        }
        return (
            <UserAvatar
                value={createdBy?.uid}
                initials={getInitials(createdBy as unknown as Record<string, unknown>)}
                src={createdBy?.photoURL}
            />
        );
    }, [createdBy, role]);

    const handleDownload = async (url: string) => {
        if (!url) return;
        try {
            await createDownloadLink(url, null);
        } catch (error) {
            // error handling
        }
    };

    const toolCalls = message.role === 'assistant' ? (message.toolCalls ?? []).filter(Boolean) : [];
    const resolvedToolCalls = useMemo<ResolvedToolCall[]>(
        () => toolCalls.map((tc) => {
            const toolMsg = tc ? toolMsgMap[tc.id] : undefined;
            return {
                id: tc?.id ?? '',
                name: tc?.function?.name ?? '',
                arguments: tc?.function?.arguments ?? '',
                result: toolMsg?.content,
                error: toolMsg?.error,
            };
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [toolMsgMap, message],
    );
    const hasToolCalls = resolvedToolCalls.length > 0;

    // Legacy behavior: when content is a string
    const hasMedia = media && media.length > 0 && media.some((m) => m !== undefined);
    const hasContent = Boolean(strippedContent);
    const validMedia = useMemo(() => media.filter((m) => m !== undefined), [media]);
    const totalBubbles = useMemo(() => {
        let count = 0;
        if (hasContent) count += 1;
        if (hasMedia) count += validMedia.length;
        return count;
    }, [hasContent, hasMedia, validMedia.length]);

    // New behavior: render multimodal content parts
    const renderContentParts = () => {
        if (!isContentArray) return null;

        const parts = content as Array<MultimodalPart>;
        const totalParts = parts.length;
        const firstTextIndex = parts.findIndex(
            (part) => part.type === 'text' && Boolean(removeActionsTokens(part.text || '')),
        );

        return parts.map((part, index) => {
            const isFirst = index === 0;
            const isLast = index === totalParts - 1;
            const isIntermediate = !isFirst && !isLast;

            // Render text part
            if (part.type === 'text') {
                const textContent = part.text || '';
                const strippedText = removeActionsTokens(textContent);

                if (!strippedText) return null;

                return (
                    <ChatBubble
                        // eslint-disable-next-line react/no-array-index-key
                        key={`text-${index}`}
                        direction={direction}
                        role={role}
                        isFirst={isFirst}
                        isLast={isLast}
                        isIntermediate={isIntermediate}
                    >
                        {adReferral && index === firstTextIndex ? (
                            <FromAdMarker referral={adReferral} />
                        ) : null}
                        <Body body={strippedText} />
                    </ChatBubble>
                );
            }

            // Render binary part
            if (part.type === 'binary') {
                const url = getBinaryPartUrl(part);
                if (!url) return null;

                const Component = getMediaComponent(part.mimeType);

                return (
                    <MediaContainer
                        // eslint-disable-next-line react/no-array-index-key
                        key={`binary-${index}`}
                        direction={direction}
                    >
                        <ChatBubble
                            direction={direction}
                            role={role}
                            isFirst={isFirst}
                            isLast={isLast}
                            isIntermediate={isIntermediate}
                        >
                            <Component url={url} filename={part.filename || part.mimeType} />
                        </ChatBubble>
                        <StyledButtonIcon
                            direction={direction}
                            size="small"
                            shaded
                            icon={<Download />}
                            onClick={() => handleDownload(url)}
                        />
                    </MediaContainer>
                );
            }

            return null;
        });
    };

    return (
        <MessageContainer direction={direction}>
            <AuthorContainer>
                {avatar}
            </AuthorContainer>
            <MessageContent direction={direction}>
                <BubbleTopContainer>
                    <AuthorName>{authorDisplayName}</AuthorName>
                    {message.createdAt && (
                        <Time>
                            <FormattedDate
                                value={message.createdAt}
                                hour="numeric"
                                minute="numeric"
                            />
                        </Time>
                    )}
                </BubbleTopContainer>

                {/* New behavior: render multimodal content parts */}
                {isContentArray && renderContentParts()}

                {/* Legacy behavior: render string content and media array */}
                {!isContentArray && (
                    <>
                        {strippedContent && (
                            <ChatBubble
                                direction={direction}
                                role={role}
                                isFirst
                                isLast={!hasMedia}
                                isIntermediate={false}
                            >
                                {adReferral ? <FromAdMarker referral={adReferral} /> : null}
                                <Body body={strippedContent} />
                            </ChatBubble>
                        )}
                        {hasMedia && (
                            <Media
                                media={media}
                                direction={direction}
                                role={role}
                                isFirst={!hasContent}
                                totalBubbles={totalBubbles}
                                contentBubbleIndex={hasContent ? 0 : -1}
                            />
                        )}
                    </>
                )}

                <BubbleBottomContainer>
                    <RenderIf isTrue={role === 'agent' && createdBy.role === 'agent'}>
                        <ReportButtonContainer>
                            <ReportButton
                                agentId={agentId}
                                context={{
                                    type: 'message',
                                    customerId: thread?.customer?.id || '',
                                    channel: thread?.from?.channel as Channel,
                                    threadId,
                                    messageId,
                                }}
                            />
                        </ReportButtonContainer>
                    </RenderIf>

                    <RenderIf isTrue={hasToolCalls}>
                        <HasRoles auth={auth} allowedRoles={['admin']}>
                            <ToolCallsButton
                                size="small"
                                variant="base"
                                borderRadius="semi-square"
                                onClick={() => setIsToolCallsModalOpen(true)}
                            >
                                <WrenchIcon className="rainbow-m-right_x-small" />
                                {`Used ${resolvedToolCalls.length} tool${resolvedToolCalls.length === 1 ? '' : 's'}`}
                            </ToolCallsButton>
                        </HasRoles>
                    </RenderIf>
                </BubbleBottomContainer>
            </MessageContent>
            <ToolCallsModal
                isOpen={isToolCallsModalOpen}
                onRequestClose={() => setIsToolCallsModalOpen(false)}
                toolCalls={resolvedToolCalls}
            />
        </MessageContainer>
    );
};

export default MessageBubble;
